import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
    setMobileCategoryOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setMenuOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const navItems = [
    { label: '首页', to: '/' },
    { label: '关于我们', to: '/about' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl">
            <i className="ri-play-circle-fill"></i>
          </span>
          <span className="font-heading text-lg md:text-xl font-extrabold text-foreground-950 tracking-tight">
            靠逼视频
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <Link
            to="/"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              location.pathname === '/' ? 'text-primary-400' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            首页
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((v) => !v)}
              className="px-4 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute top-full left-0 pt-2 w-64">
                <div className="bg-background-100 border border-background-300 rounded-lg p-2">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.id}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-200 hover:text-foreground-950 transition-colors"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 text-primary-400">
                        <i className={c.icon}></i>
                      </span>
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {navItems.slice(1).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                location.pathname === item.to ? 'text-primary-400' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频标题或简介"
              className="w-full h-10 pl-9 pr-20 rounded-full bg-background-100 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
            />
            <button
              type="submit"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-4 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md bg-background-100 border border-background-300 text-foreground-900 text-xl cursor-pointer"
          aria-label="菜单"
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-100 border-t border-background-200 px-4 py-4">
          <form onSubmit={submitSearch} className="relative w-full mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频"
              className="w-full h-10 pl-9 pr-4 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
            />
          </form>
          <Link to="/" className="block px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-200">
            首页
          </Link>
          <button
            type="button"
            onClick={() => setMobileCategoryOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-200 cursor-pointer"
          >
            分类
            <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {mobileCategoryOpen && (
            <div className="pl-3 pb-1">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-200"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded bg-background-200 text-primary-400">
                    <i className={c.icon}></i>
                  </span>
                  {c.name}
                </Link>
              ))}
            </div>
          )}
          <Link to="/about" className="block px-3 py-2.5 rounded-md text-sm text-foreground-800 hover:bg-background-200">
            关于我们
          </Link>
        </div>
      )}
    </header>
  );
}